(function () {
  function qs(sel, scope = document) { return scope.querySelector(sel); }

  let iconHandler = null;
  let lastAutoOpenAt = 0;
  const updateQueue = {};
  let requestQueue = Promise.resolve();

  function debounce(func, wait) {
    let timeout;
    return function (...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), wait);
    };
  }

  function debounceOpen(fn) {
    const now = Date.now();
    if (now - lastAutoOpenAt < 300) return false;
    lastAutoOpenAt = now;
    fn();
    return true;
  }

  function lockScroll(lock) {
    const m = lock ? 'add' : 'remove';
    document.documentElement.classList[m]('cart-drawer-lock');
    document.body.classList[m]('cart-drawer-lock');
  }

  function isEnabledFromDataset(value) {
    if (value == null) return false;
    const v = String(value).trim().toLowerCase();
    return v === 'true' || v === '1' || v === 'yes';
  }

  function formatMoney(cents) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, window.theme ? window.theme.moneyFormat : undefined);
    }
    return (Number(cents || 0) / 100).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  function parseIntSafe(value, fallback = 0) {
    const n = parseInt(value, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  function openDrawer(root) {
    if (!root) return;
    if (!isEnabledFromDataset(root.dataset.enabled)) return;

    const overlay = qs('[data-cart-overlay]', root);
    const panel = qs('.cart-drawer__panel', root);

    if (overlay) overlay.hidden = false;
    if (panel) panel.hidden = false;

    requestAnimationFrame(() => {
      root.setAttribute('data-state', 'open');
      panel && panel.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      const closeBtn = qs('[data-cart-close]', root);
      closeBtn && closeBtn.focus();
    });
  }

  function closeDrawer(root) {
    if (!root) return;
    const overlay = qs('[data-cart-overlay]', root);
    const panel = qs('.cart-drawer__panel', root);

    root.setAttribute('data-state', 'closed');
    panel && panel.setAttribute('aria-hidden', 'true');
    lockScroll(false);

    const onEnd = () => {
      overlay && (overlay.hidden = true);
      panel && (panel.hidden = true);
      panel && panel.removeEventListener('transitionend', onEnd);
    };
    panel && panel.addEventListener('transitionend', onEnd, { once: true });
  }

  function getSectionId() {
    const holder = document.getElementById('CartDrawer-Section');
    if (!holder || !holder.dataset.sectionId) {
      console.warn('[CartDrawer] Section id não encontrado para reload.');
      return null;
    }
    return holder.dataset.sectionId;
  }

  function getLinePricingFromItem(itemEl) {
    const qty = Math.max(1, parseIntSafe(itemEl?.dataset?.lineQty, 1));
    const lineOriginal = parseIntSafe(itemEl?.dataset?.lineOriginalPrice, 0);
    const lineFinal = parseIntSafe(itemEl?.dataset?.lineFinalPrice, 0);
    const discountPercent = parseIntSafe(itemEl?.dataset?.lineDiscountPercent, 0);

    let unitOriginal = qty > 0 ? Math.round(lineOriginal / qty) : 0;
    let unitFinal = qty > 0 ? Math.round(lineFinal / qty) : 0;

    if (!unitFinal) {
      unitFinal = parseIntSafe(itemEl?.dataset?.unitPrice, 0);
    }

    if (!unitOriginal) {
      unitOriginal = lineOriginal > lineFinal ? Math.round(lineOriginal / qty) : unitFinal;
    }

    return {
      qty,
      lineOriginal,
      lineFinal,
      unitOriginal,
      unitFinal,
      discountPercent
    };
  }

  function renderLinePrice(itemEl, qty, unitOriginal, unitFinal, discountPercent) {
    if (!itemEl) return;

    const wrapper = itemEl.querySelector('.item__price-wrapper');
    if (!wrapper) return;

    const compareEl = wrapper.querySelector('.item__price--compare');
    const finalEl = wrapper.querySelector('.item__price--final');
    const badgeEl = wrapper.querySelector('.item__discount-badge');

    const lineOriginal = unitOriginal * qty;
    const lineFinal = unitFinal * qty;
    const hasDiscount = lineOriginal > lineFinal && discountPercent > 0;

    itemEl.dataset.lineQty = String(qty);
    itemEl.dataset.lineOriginalPrice = String(lineOriginal);
    itemEl.dataset.lineFinalPrice = String(lineFinal);
    itemEl.dataset.lineDiscountPercent = String(discountPercent);
    itemEl.dataset.unitPrice = String(unitFinal);

    if (compareEl) {
      if (hasDiscount) {
        compareEl.textContent = formatMoney(lineOriginal);
        compareEl.style.display = '';
      } else {
        compareEl.textContent = formatMoney(lineOriginal || lineFinal);
        compareEl.style.display = 'none';
      }
    }

    if (finalEl) {
      finalEl.textContent = formatMoney(lineFinal);
    }

    if (badgeEl) {
      if (hasDiscount) {
        badgeEl.textContent = `-${discountPercent}%`;
        badgeEl.style.display = '';
      } else {
        badgeEl.textContent = '';
        badgeEl.style.display = 'none';
      }
    }

    if (hasDiscount) {
      wrapper.setAttribute('data-tag-discount-applied', 'true');
    } else {
      wrapper.removeAttribute('data-tag-discount-applied');
    }
  }

  // Aplica o HTML da seção do drawer. Se vier pronto (bundled section rendering
  // de /cart/add.js ou /cart/change.js), não faz nenhuma requisição extra.
  async function reloadCartDrawerCore(providedHtml) {
    try {
      const isOpen = document.getElementById('cart-drawer-root')?.getAttribute('data-state') === 'open';
      if (isOpen && window.__cartDiscountInteracting) return;

      const sectionId = getSectionId();
      if (!sectionId) return;

      const currentRoot = document.getElementById('cart-drawer-root');
      const currentContent = currentRoot && currentRoot.querySelector('.cart-drawer__content');

      const currentAccordion = currentContent && currentContent.querySelector('.cart-discount__accordion');
      const wasOpen = !!(currentAccordion && currentAccordion.hasAttribute('open'));
      const prevInput = currentContent && currentContent.querySelector('.cart-discount__input');
      const prevInputValue = prevInput ? prevInput.value : '';
      const prevInputFocus = prevInput && document.activeElement === prevInput;

      let html = providedHtml;
      if (!html) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionId);
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return;
        const json = await res.json();
        html = json[sectionId];
      }
      if (!html) return;

      const tmp = document.createElement('div');
      tmp.innerHTML = html;

      const incomingContent = tmp.querySelector('#CartDrawer-Section .cart-drawer__content');

      if (incomingContent && currentContent) {
        const currentEmpty = currentContent.querySelector('.cart-drawer__empty-content');
        const incomingEmpty = incomingContent.querySelector('.cart-drawer__empty-content');

        const optionalSelectors = ['.cart-gifts', '.cart-drawer__recommendations'];
        let structureChanged = (!!currentEmpty !== !!incomingEmpty);

        if (!structureChanged) {
          for (const sel of optionalSelectors) {
            if (!!currentContent.querySelector(sel) !== !!incomingContent.querySelector(sel)) {
              structureChanged = true;
              break;
            }
          }
        }

        if (structureChanged) {
          currentContent.innerHTML = incomingContent.innerHTML;
        } else {
          const curItems = currentContent.querySelector('.cart-drawer__items');
          const incItems = incomingContent.querySelector('.cart-drawer__items');
          if (curItems && incItems) {
            updateItemsGranular(curItems, incItems);
          } else if (currentEmpty && incomingEmpty) {
            if (currentEmpty.textContent !== incomingEmpty.textContent) {
              currentEmpty.textContent = incomingEmpty.textContent;
            }
          }

          const curFooter = currentContent.querySelector('.cart-drawer__footer');
          const incFooter = incomingContent.querySelector('.cart-drawer__footer');
          if (curFooter && incFooter) {
            updateFooterGranular(curFooter, incFooter);
          }

          ['.cart-gifts', '.cart-drawer__recommendations', '#BrindesJson', '.cart-gifts-modal'].forEach(sel => {
            const cEl = currentContent.querySelector(sel);
            const iEl = incomingContent.querySelector(sel);
            if (cEl && iEl && cEl.innerHTML !== iEl.innerHTML) {
              cEl.innerHTML = iEl.innerHTML;
            }
          });
        }

        const newAccordion = currentContent.querySelector('.cart-discount__accordion');
        if (newAccordion && wasOpen) {
          newAccordion.setAttribute('open', '');
        }
        const newInput = currentContent.querySelector('.cart-discount__input');
        if (newInput) {
          if (prevInputValue) newInput.value = prevInputValue;
          if (prevInputFocus) newInput.focus();
        }
      }

      if (window.CART_RECOMMENDATIONS && typeof window.CART_RECOMMENDATIONS.refresh === 'function') {
        window.CART_RECOMMENDATIONS.refresh();
      }

      if (!providedHtml) fetchCartAndUpdateCount();
      document.dispatchEvent(new CustomEvent('cart-drawer:updated'));
    } catch (e) {
      console.warn('Falha ao recarregar o Cart Drawer:', e);
    }
  }

  const debouncedReloadCartDrawer = debounce(() => reloadCartDrawerCore(), 250);
  let lastFreshHtml = null; // HTML recebido junto com a última alteração do carrinho

  window.reloadCartDrawer = debouncedReloadCartDrawer;

  async function refreshDiscountBlocks(providedCartData, providedHtml) {
    try {
      if (window.__discountRefreshing) return;
      window.__discountRefreshing = true;

      const sectionId = getSectionId();
      if (!sectionId) return;

      const currentRoot = document.getElementById('cart-drawer-root');
      const currentContent = currentRoot && currentRoot.querySelector('.cart-drawer__content');
      if (!currentContent) return;

      const currentAccordion = currentContent.querySelector('.cart-discount__accordion');
      const wasOpen = !!(currentAccordion && currentAccordion.hasAttribute('open'));
      const prevInput = currentContent.querySelector('.cart-discount__input');
      const prevInputValue = prevInput ? prevInput.value : '';
      const prevInputFocus = prevInput && document.activeElement === prevInput;
      const prevScrollTop = currentContent ? currentContent.scrollTop : 0;

      let html = providedHtml;
      if (!html) {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', sectionId);
        const res = await fetch(url, { credentials: 'same-origin' });
        if (!res.ok) return;
        const json = await res.json();
        html = json[sectionId];
      }
      if (!html) return;

      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const incomingScope = tmp.querySelector('#CartDrawer-Section .cart-drawer__content');
      if (!incomingScope) return;

      const targets = [
        '.cart-discount__applied-discounts',
        '.cart-drawer__subtotal-prices',
        '.cart-drawer__global-discounts',
      ];

      targets.forEach((sel) => {
        const incomingEl = incomingScope.querySelector(sel);
        const currentEl = currentContent.querySelector(sel);

        if (incomingEl && currentEl) {
          currentEl.innerHTML = incomingEl.innerHTML;
        } else if (incomingEl && !currentEl && sel === '.cart-drawer__global-discounts') {
          const subtotalWrapper = currentContent.querySelector('.cart-drawer__subtotal');
          if (subtotalWrapper) {
            subtotalWrapper.parentElement.insertBefore(incomingEl, subtotalWrapper);
          }
        } else if (!incomingEl && currentEl && sel === '.cart-drawer__global-discounts') {
          currentEl.remove();
        }
      });

      const incItemsContainer = incomingScope.querySelector('.cart-drawer__items');
      const curItemsContainer = currentContent.querySelector('.cart-drawer__items');

      if (incItemsContainer && curItemsContainer) {
        updateItemsGranular(curItemsContainer, incItemsContainer);
      }

      if (prevScrollTop) currentContent.scrollTop = prevScrollTop;

      const newAccordion = currentContent.querySelector('.cart-discount__accordion');
      if (newAccordion && wasOpen) newAccordion.setAttribute('open', '');
      const newInput = currentContent.querySelector('.cart-discount__input');
      if (newInput) {
        if (prevInputValue && newInput.value !== prevInputValue) newInput.value = prevInputValue;
        if (prevInputFocus) newInput.focus();
      }

      if (providedCartData && typeof providedCartData.item_count === 'number') {
        updateCartCount(providedCartData.item_count);
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: providedCartData } }));
        } catch (e) {}
      } else {
        await fetchCartAndUpdateCount();
      }

      document.dispatchEvent(new CustomEvent('cart-drawer:updated'));
    } catch (e) {
      console.warn('Falha ao atualizar blocos de desconto do Cart Drawer:', e);
    } finally {
      window.__discountRefreshing = false;
    }
  }

  function updateItemsGranular(curContainer, incContainer) {
    const incItems = Array.from(incContainer.querySelectorAll('.cart-drawer__item'));
    const curItems = Array.from(curContainer.querySelectorAll('.cart-drawer__item'));

    const curMap = new Map();
    curItems.forEach(el => curMap.set(el.dataset.lineKey, el));

    curItems.forEach(curEl => {
      const key = curEl.dataset.lineKey;
      const incEl = incItems.find(i => i.dataset.lineKey === key);

      if (updateQueue[key] && updateQueue[key].token && curEl.style.display === 'none') {
        return;
      }

      if (incEl) {
        [
          'lineQty',
          'unitPrice',
          'lineOriginalPrice',
          'lineFinalPrice',
          'lineDiscountPercent',
          'variantId'
        ].forEach(attr => {
          if (incEl.dataset[attr] != null) {
            curEl.dataset[attr] = incEl.dataset[attr];
          }
        });

        const incQty = incEl.dataset.lineQty;
        const curQty = curEl.dataset.lineQty;
        if (!updateQueue[key] && incQty !== curQty) {
          curEl.dataset.lineQty = incQty;
          const qtyVal = curEl.querySelector('[data-qty-value]');
          if (qtyVal && qtyVal.textContent !== incQty) qtyVal.textContent = incQty;

          const btnMinus = curEl.querySelector('[data-qty-minus]');
          if (btnMinus) {
            if (parseIntSafe(incQty) <= 1) btnMinus.setAttribute('disabled', '');
            else btnMinus.removeAttribute('disabled');
          }
        }

        const curPriceWrapper = curEl.querySelector('.item__price-wrapper');
        const incPriceWrapper = incEl.querySelector('.item__price-wrapper');
        if (curPriceWrapper && incPriceWrapper && curPriceWrapper.innerHTML !== incPriceWrapper.innerHTML) {
          curPriceWrapper.innerHTML = incPriceWrapper.innerHTML;
          if (incPriceWrapper.hasAttribute('data-tag-discount-applied')) {
            curPriceWrapper.setAttribute('data-tag-discount-applied', 'true');
          } else {
            curPriceWrapper.removeAttribute('data-tag-discount-applied');
          }
        }

        const curVariant = curEl.querySelector('.item__variant');
        const incVariant = incEl.querySelector('.item__variant');
        if (curVariant && incVariant && curVariant.innerHTML !== incVariant.innerHTML) {
          curVariant.innerHTML = incVariant.innerHTML;
        }
      }
    });

    incItems.forEach(incEl => {
      const key = incEl.dataset.lineKey;
      if (!curMap.has(key)) {
        const newNode = document.importNode(incEl, true);
        curContainer.insertBefore(newNode, curContainer.firstChild);
      }
    });

    curItems.forEach(curEl => {
      const key = curEl.dataset.lineKey;
      const inIncoming = incItems.find(i => i.dataset.lineKey === key);
      if (!inIncoming && !updateQueue[key]) {
        curEl.remove();
      }
    });
  }

  function updateFooterGranular(curFooter, incFooter) {
    const curTotal = curFooter.querySelector('[data-cart-total]');
    const incTotal = incFooter.querySelector('[data-cart-total]');
    if (curTotal && incTotal) {
      if (curTotal.textContent !== incTotal.textContent) {
        curTotal.textContent = incTotal.textContent;
      }
      Array.from(incTotal.attributes).forEach(attr => {
        if (attr.name.startsWith('data-')) {
          curTotal.setAttribute(attr.name, attr.value);
        }
      });
    }

    const curCompare = curFooter.querySelector('.cart-drawer__subtotal-compare');
    const incCompare = incFooter.querySelector('.cart-drawer__subtotal-compare');

    if (incCompare && incCompare.textContent.trim()) {
      if (curCompare) {
        if (curCompare.textContent !== incCompare.textContent) {
          curCompare.textContent = incCompare.textContent;
        }
        curCompare.style.display = '';
      } else {
        const newCompare = document.createElement('span');
        newCompare.className = 'cart-drawer__subtotal-compare';
        newCompare.textContent = incCompare.textContent;
        const pricesContainer = curFooter.querySelector('.cart-drawer__subtotal-prices');
        if (pricesContainer && curTotal) {
          pricesContainer.insertBefore(newCompare, curTotal);
        }
      }
    } else if (curCompare) {
      curCompare.style.display = 'none';
    }
  }

  window.CART_DRAWER = {
    reload: debouncedReloadCartDrawer,
    refreshDiscounts: refreshDiscountBlocks,
    updateCount: fetchCartAndUpdateCount
  };

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-drawer-count]').forEach((el) => {
      el.textContent = String(count);
      el.hidden = !(Number(count) > 0);
    });
    const bubbleHost = document.getElementById('cart-icon-bubble');
    if (!bubbleHost) return;

    let bubble = bubbleHost.querySelector('.cart-count-bubble');
    const shouldShow = Number(count) > 0;
    const text = Number(count) < 100 ? String(count) : '99+';

    if (!bubble && shouldShow) {
      bubble = document.createElement('span');
      bubble.className = 'cart-count-bubble';
      bubbleHost.querySelector('.svg-wrapper')?.appendChild(bubble);
    }

    if (bubble) {
      if (shouldShow) {
        bubble.textContent = text;
        bubble.style.display = '';
      } else {
        bubble.textContent = '';
        bubble.style.display = 'none';
      }
    }
  }

  async function fetchCartAndUpdateCount() {
    try {
      const res = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin'
      });
      if (!res.ok) return;
      const cart = await res.json();
      if (cart && typeof cart.item_count === 'number') {
        updateCartCount(cart.item_count);
        try {
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        } catch (e) {}
      }
    } catch (e) {
      console.warn('Não foi possível obter /cart.js para atualizar o contador.', e);
    }
  }

  function bindCartIconIntercept(root) {
    const icon = document.getElementById('cart-icon-bubble');
    if (!icon) return;

    if (iconHandler) {
      icon.removeEventListener('click', iconHandler);
      iconHandler = null;
    }

    const enabled = isEnabledFromDataset(root?.dataset?.enabled);

    if (!enabled) {
      const clone = icon.cloneNode(true);
      icon.replaceWith(clone);
      return;
    }

    iconHandler = (ev) => {
      if (!isEnabledFromDataset(root?.dataset?.enabled)) return;
      ev.preventDefault();
      openDrawer(root);
      debouncedReloadCartDrawer();
    };

    icon.addEventListener('click', iconHandler, { passive: false });
  }

  function bindAutoOpenOnAdd(root) {
    document.addEventListener('submit', async (e) => {
      const form = e.target;
      if (!form.matches('form[action*="/cart/add"]')) return;
      if (!isEnabledFromDataset(root?.dataset?.enabled)) return;

      e.preventDefault();
      const formData = new FormData(form);
      const submitBtn = form.querySelector('[type="submit"]');

      if (submitBtn) {
        submitBtn.setAttribute('aria-busy', 'true');
        submitBtn.disabled = true;
      }

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });

        if (!res.ok) {
          try {
            const errorData = await res.json();
            const msg = errorData.description || 'Não foi possível adicionar ao carrinho.';
            if (window.showToast) {
              window.showToast(msg, { type: 'error', duration: 4000 });
            }
          } catch (e) {
            console.error('Erro ao processar resposta de erro do carrinho', e);
          }
          return;
        }

        await fetchCartAndUpdateCount();
        debounceOpen(() => openDrawer(root));
        setTimeout(() => debouncedReloadCartDrawer(), 100);
      } catch (err) {
        console.error('Erro ao adicionar produto ao carrinho:', err);
      } finally {
        if (submitBtn) {
          submitBtn.removeAttribute('aria-busy');
          submitBtn.disabled = false;
        }
      }
    });
  }

  function interceptNetworkAdds(root) {
    if (!window._cartDrawerPatched) {
      const _fetch = window.fetch;
      window.fetch = async function (input, init) {
        let url = typeof input === 'string' ? input : (input?.url || '');
        let method = (init?.method || '').toUpperCase();

        const isAdd = method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(url);
        const h = init && init.headers;
        const isQuickAdd = !!(h && (h['X-Ially-Quick-Add'] || (typeof h.get === 'function' && h.get('X-Ially-Quick-Add'))));
        const resp = await _fetch.apply(this, arguments);

        if (isAdd && !isQuickAdd && isEnabledFromDataset(root?.dataset?.enabled)) {
          if (resp && resp.ok) {
            // em segundo plano: devolve a resposta na hora para quem chamou
            fetchCartAndUpdateCount().catch(() => {});
            debounceOpen(() => openDrawer(root));
            setTimeout(() => debouncedReloadCartDrawer(), 100);
          }
        }
        return resp;
      };

      const _open = XMLHttpRequest.prototype.open;
      const _send = XMLHttpRequest.prototype.send;
      const xhrTrack = new WeakMap();

      XMLHttpRequest.prototype.open = function (method, url) {
        xhrTrack.set(this, { method: (method || '').toUpperCase(), url: String(url || '') });
        return _open.apply(this, arguments);
      };

      XMLHttpRequest.prototype.send = function () {
        const meta = xhrTrack.get(this) || {};
        const isAdd = meta.method === 'POST' && /\/cart\/add(\.js)?(\?|$)/.test(meta.url);

        if (isAdd) {
          this.addEventListener('load', async () => {
            if (this.status >= 200 && this.status < 300 && isEnabledFromDataset(root?.dataset?.enabled)) {
              try {
                await fetchCartAndUpdateCount();
                debounceOpen(() => openDrawer(root));
                setTimeout(() => debouncedReloadCartDrawer(), 100);
              } catch (e) {
                console.warn('Intercept XHR add -> erro ao sincronizar:', e);
              }
            }
          });
        }
        return _send.apply(this, arguments);
      };

      window._cartDrawerPatched = true;
    }
  }

  function showItemError(itemEl, message) {
    if (!itemEl) return;
    const el = itemEl.querySelector('[data-cart-item-error]');
    if (!el) return;
    el.textContent = message || '';
    el.hidden = !message;
  }

  function clearItemError(itemEl) {
    showItemError(itemEl, '');
  }

  function getVariantsFromItem(itemEl) {
    if (!itemEl) return [];
    const script = itemEl.querySelector('[data-cart-item-variants]');
    if (!script) return [];
    try { return JSON.parse(script.textContent || '[]'); }
    catch (e) { return []; }
  }

  function getInventoryMapFromItem(itemEl) {
    if (!itemEl) return {};
    const script = itemEl.querySelector('[data-inventory-map]');
    if (!script) return {};
    try { return JSON.parse(script.textContent || '{}'); }
    catch (e) { return {}; }
  }

  async function handleVariantChange(root, itemEl) {
    if (!itemEl) return;
    clearItemError(itemEl);

    const lineKey = itemEl.dataset.lineKey;
    const lineQty = parseIntSafe(itemEl.dataset.lineQty, 1);
    const variants = getVariantsFromItem(itemEl);
    if (!variants.length) return;

    const selects = Array.from(itemEl.querySelectorAll('.cart-item__select'));
    if (!selects.length) return;

    const listEl = itemEl.parentElement;
    const previousIndex = listEl ? Array.from(listEl.children).indexOf(itemEl) : -1;

    const selectedOptionsByIndex = {};
    selects.forEach((sel) => {
      const posStr = sel.getAttribute('data-option-position');
      if (posStr == null) return;
      const idx = parseIntSafe(posStr, -1);
      if (idx < 0) return;
      selectedOptionsByIndex[idx] = sel.value;
    });

    if (!Object.keys(selectedOptionsByIndex).length) return;

    const maxIndex = Math.max.apply(null, Object.keys(selectedOptionsByIndex).map(Number));
    const selectedOptions = [];
    for (let i = 0; i <= maxIndex; i++) {
      selectedOptions[i] = selectedOptionsByIndex[i] || null;
    }

    const newVariant = variants.find((v) => {
      if (!v || !Array.isArray(v.options)) return false;
      for (let i = 0; i < selectedOptions.length; i++) {
        if (!selectedOptions[i]) continue;
        if (v.options[i] !== selectedOptions[i]) return false;
      }
      return true;
    });

    if (!newVariant || !newVariant.available) {
      showItemError(itemEl, 'Combinação indisponível.');
      selects.forEach((sel) => {
        if (sel.dataset.prevValue) sel.value = sel.dataset.prevValue;
      });
      return;
    }

    if (String(newVariant.id) === String(itemEl.dataset.variantId)) return;

    itemEl.classList.add('is-updating');
    itemEl.dataset.changingVariant = 'true';

    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: 0 })
      });

      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: newVariant.id, quantity: lineQty })
      });

      await fetchCartAndUpdateCount();
      await debouncedReloadCartDrawer();

      setTimeout(() => {
        if (previousIndex >= 0) {
          const rootNode = document.getElementById('cart-drawer-root');
          const newList = rootNode && rootNode.querySelector('.cart-drawer__items');
          if (newList) {
            const items = Array.from(newList.querySelectorAll('.cart-drawer__item'));
            const newItem = items.find((li) => String(li.dataset.variantId) === String(newVariant.id));
            if (newItem) {
              const currentIndex = items.indexOf(newItem);
              if (currentIndex !== previousIndex) {
                const children = Array.from(newList.children);
                if (currentIndex === 0 && previousIndex > 0) {
                  const ref = children[previousIndex + 1];
                  if (ref) newList.insertBefore(newItem, ref);
                  else newList.appendChild(newItem);
                }
              }
            }
          }
        }
      }, 150);
    } catch (e) {
      console.error(e);
      showItemError(itemEl, 'Erro ao atualizar variação.');
    } finally {
      itemEl.classList.remove('is-updating');
    }
  }

  function bindVariantSelectHandlers(root) {
    if (!root) return;
    root.addEventListener('focusin', (event) => {
      const sel = event.target;
      if (!sel.classList || !sel.classList.contains('cart-item__select')) return;
      sel.dataset.prevValue = sel.value;
    });
    root.addEventListener('change', (event) => {
      const sel = event.target;
      if (!sel.classList || !sel.classList.contains('cart-item__select')) return;
      const itemEl = sel.closest('.cart-drawer__item');
      if (!itemEl) return;
      handleVariantChange(root, itemEl);
    });
  }

  async function performLineUpdate(lineKey, newQty, itemEl, token) {
    requestQueue = requestQueue.then(async () => {
      try {
        if (updateQueue[lineKey] && updateQueue[lineKey].token && updateQueue[lineKey].token !== token) {
          return;
        }

        const sectionIdForChange = getSectionId();
        const changeBody = { id: lineKey, quantity: newQty };
        if (sectionIdForChange) {
          changeBody.sections = sectionIdForChange;
          changeBody.sections_url = window.location.pathname;
        }
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(changeBody),
          keepalive: true
        });

        if (!res.ok) {
          if (itemEl && itemEl.style.display === 'none') {
            itemEl.style.display = '';
            itemEl.removeAttribute('aria-hidden');
          }

          let msg = 'Erro ao atualizar quantidade.';
          try {
            const data = await res.json();
            if (data && data.description) {
              const match = data.description.match(/Only\s+(\d+)\s+item/i);
              if (match) msg = `Restam apenas ${match[1]} unidades.`;
              else if (data.description.includes('availability') || data.description.includes('Only')) {
                msg = 'Estoque máximo atingido.';
              }
            }
          } catch (_) {}

          showItemError(itemEl, msg);
          await debouncedReloadCartDrawer();
        } else {
          const cartData = await res.json();
          const freshHtml = cartData && cartData.sections && sectionIdForChange ? cartData.sections[sectionIdForChange] : null;
          if (freshHtml) lastFreshHtml = freshHtml;

          updateCartCount(cartData.item_count);

          const hasPendingRequests = Object.keys(updateQueue).length > 1;
          if (!hasPendingRequests) {
            await refreshDiscountBlocks(cartData, freshHtml);
          } else {
            updateCartCount(cartData.item_count);
            if (itemEl && !cartData.items.find(i => i.key === lineKey) && newQty === 0) {
              itemEl.remove();
            }
          }

          try {
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cartData } }));
          } catch (e) {}

          if (itemEl && cartData.items) {
            const updatedItem = cartData.items.find(i => i.key === lineKey);

            if (updatedItem) {
              if (newQty === 0 && hasPendingRequests) {
                return;
              }

              if (updatedItem.quantity !== newQty && newQty !== 0) {
                const qtyVal = itemEl.querySelector('[data-qty-value]');
                if (qtyVal) qtyVal.textContent = updatedItem.quantity;
                itemEl.dataset.lineQty = String(updatedItem.quantity);

                const btnMinus = itemEl.querySelector('[data-qty-minus]');
                if (btnMinus) {
                  if (updatedItem.quantity <= 1) btnMinus.setAttribute('disabled', '');
                  else btnMinus.removeAttribute('disabled');
                }

                const incomingLineOriginal = updatedItem.original_line_price || updatedItem.final_line_price || 0;
                const incomingLineFinal = updatedItem.final_line_price || 0;
                const discountPercent = incomingLineOriginal > 0
                  ? Math.round(((incomingLineOriginal - incomingLineFinal) * 100) / incomingLineOriginal)
                  : 0;

                const unitOriginal = updatedItem.quantity > 0 ? Math.round(incomingLineOriginal / updatedItem.quantity) : 0;
                const unitFinal = updatedItem.quantity > 0 ? Math.round(incomingLineFinal / updatedItem.quantity) : 0;

                renderLinePrice(itemEl, updatedItem.quantity, unitOriginal, unitFinal, discountPercent);
              } else if (newQty === 0) {
                if (itemEl.style.display === 'none') {
                  itemEl.style.display = '';
                  itemEl.removeAttribute('aria-hidden');
                  showItemError(itemEl, 'Não foi possível remover o item.');
                }
              }
            } else if (newQty > 0) {
              await debouncedReloadCartDrawer();
            } else {
              itemEl.remove();
              const list = document.querySelector('.cart-drawer__items');
              if (list && list.children.length === 0) {
                await debouncedReloadCartDrawer();
              }
            }
          }
        }
      } catch (e) {
        console.error(e);
        if (itemEl && itemEl.style.display === 'none') {
          itemEl.style.display = '';
          itemEl.removeAttribute('aria-hidden');
        }
        showItemError(itemEl, 'Erro de conexão.');
        await debouncedReloadCartDrawer();
      } finally {
        if (itemEl) itemEl.classList.remove('is-updating');
        if (updateQueue[lineKey] && updateQueue[lineKey].token === token) {
          delete updateQueue[lineKey];
        }
      }
    }).catch(e => {
      console.error('Falha na fila de requisições do carrinho:', e);
    }).finally(() => {
      if (Object.keys(updateQueue).length === 0) {
        if (lastFreshHtml) {
          const html = lastFreshHtml;
          lastFreshHtml = null;
          reloadCartDrawerCore(html); // usa o HTML que já chegou, sem nova requisição
        } else {
          debouncedReloadCartDrawer();
        }
        setCheckoutLoading(false);
      }
    });

    return requestQueue;
  }

  function changeLineQuantity(lineKey, newQty, itemEl) {
    if (!lineKey || !itemEl) return;

    if (updateQueue[lineKey] && updateQueue[lineKey].timeout) {
      clearTimeout(updateQueue[lineKey].timeout);
    }

    const pricing = getLinePricingFromItem(itemEl);
    const currentQty = pricing.qty;

    itemEl.dataset.lineQty = String(newQty);

    const qtyVal = itemEl.querySelector('[data-qty-value]');
    if (qtyVal) qtyVal.textContent = newQty;

    const btnMinus = itemEl.querySelector('[data-qty-minus]');
    if (btnMinus) {
      if (newQty <= 1) btnMinus.setAttribute('disabled', '');
      else btnMinus.removeAttribute('disabled');
    }

    if (newQty === 0) {
      itemEl.style.opacity = '0.5';
    } else {
      itemEl.style.opacity = '';
    }

    renderLinePrice(
      itemEl,
      newQty,
      pricing.unitOriginal,
      pricing.unitFinal,
      pricing.discountPercent
    );

    const subtotalEl = document.querySelector('[data-cart-total-raw]');
    if (subtotalEl) {
      const diff = newQty - currentQty;
      const currentSubtotal = parseIntSafe(subtotalEl.getAttribute('data-cart-total-raw'), 0);
      const newSubtotal = currentSubtotal + (diff * pricing.unitFinal);

      subtotalEl.textContent = formatMoney(newSubtotal);
      subtotalEl.setAttribute('data-cart-total-raw', String(newSubtotal));
    }

    setCheckoutLoading(true);

    const updateToken = Date.now() + Math.random();
    updateQueue[lineKey] = {
      token: updateToken,
      timeout: setTimeout(() => {
        performLineUpdate(lineKey, newQty, itemEl, updateToken);
      }, 500)
    };
  }

  function setCheckoutLoading(isLoading) {
    const checkoutBtns = document.querySelectorAll('button[name="checkout"], input[name="checkout"], a[href="/checkout"], .cart__checkout-button');
    checkoutBtns.forEach(btn => {
      if (isLoading) {
        if (!btn.dataset.originalText) {
          btn.dataset.originalText = btn.textContent;
        }
        btn.setAttribute('disabled', 'disabled');
        btn.classList.add('is-loading');
        btn.style.opacity = '0.6';
        btn.style.pointerEvents = 'none';
        btn.textContent = 'Processando...';
      } else {
        btn.removeAttribute('disabled');
        btn.classList.remove('is-loading');
        btn.style.opacity = '';
        btn.style.pointerEvents = '';
        if (btn.dataset.originalText) {
          btn.textContent = btn.dataset.originalText;
        }
      }
    });
  }

  // Recolhe a linha em ~200ms e depois esconde (display:none mantém a lógica existente)
  function collapseItem(itemEl) {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !itemEl.animate) { itemEl.style.display = 'none'; return; }
    const h = itemEl.offsetHeight;
    itemEl.style.overflow = 'hidden';
    const anim = itemEl.animate(
      [
        { opacity: 1, height: h + 'px', transform: 'translateX(0)' },
        { opacity: 0, height: h + 'px', transform: 'translateX(24px)', offset: 0.55 },
        { opacity: 0, height: '0px', paddingBottom: '0px', marginTop: '0px', borderBottomWidth: '0px', transform: 'translateX(24px)' }
      ],
      { duration: 220, easing: 'cubic-bezier(.2,.7,.2,1)' }
    );
    const hide = () => { itemEl.style.display = 'none'; itemEl.style.overflow = ''; };
    anim.onfinish = hide;
    anim.oncancel = hide;
  }

  function bindQuantityHandlers(root) {
    if (!root) return;

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('.item__qty-btn');
      if (!btn) return;

      const itemEl = btn.closest('.cart-drawer__item');
      if (!itemEl) return;

      const lineKey = itemEl.dataset.lineKey;
      const currentQty = parseIntSafe(itemEl.dataset.lineQty, 0);
      const isPlus = btn.dataset.qtyPlus !== undefined;
      const newQty = isPlus ? currentQty + 1 : currentQty - 1;

      if (newQty < 0) return;

      if (isPlus) {
        const variantId = itemEl.dataset.variantId;
        const invMap = getInventoryMapFromItem(itemEl);
        const inv = invMap[variantId];

        if (inv && inv.management === 'shopify' && inv.policy === 'deny') {
          if (newQty > inv.qty) {
            if (window.showToast && window.matchMedia('(min-width: 769px)').matches) {
              window.showToast(`Somente ${inv.qty} itens foram adicionados ao seu carrinho devido à disponibilidade`, { type: 'error', duration: 4000 });
            }
            showItemError(itemEl, `Quantidade Máxima: ${inv.qty}`);
            return;
          }
        }
      }

      changeLineQuantity(lineKey, newQty, itemEl);
    });

    root.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-item-remove]')) {
        const itemEl = e.target.closest('.cart-drawer__item');
        if (!itemEl) return;

        const lineKey = itemEl.dataset.lineKey;
        const pricing = getLinePricingFromItem(itemEl);
        if (itemEl.dataset.removing === 'true') return;
        itemEl.dataset.removing = 'true';

        collapseItem(itemEl);
        itemEl.setAttribute('aria-hidden', 'true');
        if (window.IallyCartFly) window.IallyCartFly.bump(-pricing.qty);

        const subtotalEl = document.querySelector('[data-cart-total-raw]');
        if (subtotalEl) {
          const currentSubtotal = parseIntSafe(subtotalEl.getAttribute('data-cart-total-raw'), 0);
          const totalItemPrice = pricing.unitFinal * pricing.qty;
          const newSubtotal = Math.max(0, currentSubtotal - totalItemPrice);

          subtotalEl.textContent = formatMoney(newSubtotal);
          subtotalEl.setAttribute('data-cart-total-raw', String(newSubtotal));
        }

        if (updateQueue[lineKey] && updateQueue[lineKey].timeout) {
          clearTimeout(updateQueue[lineKey].timeout);
        }

        setCheckoutLoading(true);
        const updateToken = Date.now() + Math.random();
        updateQueue[lineKey] = { token: updateToken };

        performLineUpdate(lineKey, 0, itemEl, updateToken).catch(() => {
          itemEl.style.display = '';
          itemEl.removeAttribute('aria-hidden');
          delete updateQueue[lineKey];
        }).finally(() => {
          delete itemEl.dataset.removing;
        });
      }
    });
  }

  function init() {
    const root = document.getElementById('cart-drawer-root');
    if (!root) return;

    const closeBtn = qs('[data-cart-close]', root);
    const overlay = qs('[data-cart-overlay]', root);

    const close = () => closeDrawer(root);

    if (closeBtn) closeBtn.addEventListener('click', close);
    if (overlay) overlay.addEventListener('click', close);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && root.dataset.state === 'open') close();
    });

    bindCartIconIntercept(root);
    bindAutoOpenOnAdd(root);
    interceptNetworkAdds(root);
    bindVariantSelectHandlers(root);
    bindQuantityHandlers(root);

    window.CART_DRAWER = {
      open: () => openDrawer(root),
      close: () => closeDrawer(root),
      reload: debouncedReloadCartDrawer,
      applyHtml: (html) => reloadCartDrawerCore(html),
      sectionId: getSectionId,
      refreshDiscounts: refreshDiscountBlocks,
      updateCount: fetchCartAndUpdateCount
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();